var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect } from 'react';
import { bulletinboardsApi } from 'apis/bulletinboards';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
/** This is the hard limit on how many bulletins a user can add */
export const MAX_ALLOWED_BULLETINS = 9;
export function useBulletins({ boardId, messengerId, onBeforeAdd, }) {
    var _a;
    const dispatch = useAppDispatch();
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const [updateBulletin, { isLoading: isUpdateLoading }] = bulletinboardsApi.useUpdateBulletinMutation();
    const [deleteBulletin, { isLoading: isRemoveLoading }] = bulletinboardsApi.useDeleteBulletinMutation();
    const [createBulletin, { isLoading: isCreateLoading }] = bulletinboardsApi.useCreateBulletinMutation();
    const notifyError = useCallback((message) => notify({ title: message, type: 'error' }), [notify]);
    const { isFetching, isError, data } = bulletinboardsApi.useReadBulletinsQuery({
        boardId,
    });
    useEffect(() => {
        if (isError) {
            notifyError('Sorry there has been problem loading your bulletins, please try again.');
        }
    }, [isError, notifyError]);
    const bulletins = (_a = data === null || data === void 0 ? void 0 : data.data.bulletins) !== null && _a !== void 0 ? _a : [];
    const onDelete = (bulletin) => __awaiter(this, void 0, void 0, function* () {
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: 'Delete content',
                    description: `Are you sure you want to delete ${bulletin.title} This action can’t be undone.`,
                    confirm: {
                        label: 'Yes, delete',
                    },
                },
            },
        }));
        if (!isConfirmed)
            return;
        try {
            yield deleteBulletin({ boardId, bulletinId: bulletin.id });
        }
        catch (error) {
            notifyError('Sorry, there has been an error deleting your bulletin, please try again.');
        }
    });
    const setEnabled = (bulletin, active) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield updateBulletin({
                boardId,
                bulletinId: bulletin.id,
                update: {
                    active,
                },
            });
        }
        catch (error) {
            notifyError('Updating bulletin failed, please try again');
        }
    });
    const onEdit = (bulletin) => {
        navigate({
            path: '/settings/dixa-messenger/appearance/:messengerId/bulletin/:bulletinId',
            params: { path: { bulletinId: bulletin.id, messengerId } },
        });
    };
    const onAdd = () => __awaiter(this, void 0, void 0, function* () {
        try {
            onBeforeAdd === null || onBeforeAdd === void 0 ? void 0 : onBeforeAdd();
            const order = bulletins.length > 0 ? bulletins[bulletins.length - 1].order + 1 : 1;
            const resp = yield createBulletin({ boardId, title: '', order });
            if ('data' in resp) {
                const newBulletin = resp.data;
                navigate({
                    path: '/settings/dixa-messenger/appearance/:messengerId/bulletin/:bulletinId',
                    params: { path: { bulletinId: newBulletin.id, messengerId } },
                });
            }
        }
        catch (error) {
            notifyError('Creating bulletin failed');
        }
    });
    const onReorder = (bulletins) => __awaiter(this, void 0, void 0, function* () {
        const updatedBulletins = bulletins.map((b, i) => (Object.assign(Object.assign({}, b), { order: i + 1 })));
        const promises = updatedBulletins.map((bulletin) => {
            return updateBulletin({
                boardId,
                bulletinId: bulletin.id,
                update: {
                    order: bulletin.order,
                },
            });
        });
        try {
            yield Promise.all(promises);
        }
        catch (error) {
            notifyError('Updating order failed, please try again');
        }
    });
    return {
        hasReadError: isError,
        isFetching,
        isUpdating: isCreateLoading || isUpdateLoading || isRemoveLoading,
        bulletins,
        canAddMore: bulletins.length < MAX_ALLOWED_BULLETINS,
        setEnabled,
        onDelete,
        onEdit,
        onAdd,
        onReorder,
    };
}
